import React, { lazy, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { object } from 'yup';
import moment from 'moment';
import { isdvirOnly } from 'globalutils/fmUtils';
import RenderComponent from './RenderComponent';
import { pageUrls } from 'globalconfig/constants/keys';
import { schema } from './schema';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));

const WorkOrderForm = ({
  apiStatus,
  history,
  saveDocument,
  deleteDocument,
  callback,
  i18n,
  type,
  downloadDocument,
  logBook,
  Styled,
  workOrders,
  saveAddWorkOrders,
  getDocumentToken,
  viewReport,
  receipt,
  clearState,
  setOpenPhoto,
  ModalObjectPhoto,
  setModalObjectPhoto,
  viewOnly,
}) => {
  useEffect(() => {
    return () => {
      clearState();
    };
  }, []);

  useEffect(() => {
    const url = receipt ? receipt : '';
    if (typeof url === 'string') document.getElementById(`downloadDocument`).src = url;
  }, [receipt]);

  const objectArrayFromDiffFields = (list, field, value, callback) => {
    return list?.length
      ? list.map((item) => {
          if (callback) return callback(item, field, value);
          else
            return {
              label: item[value],
              value: item[field],
              vin: item['vin'] || '',
            };
        })
      : [];
  };

  const partExists = (id) => {
    if (workOrders?.getData?.workOrderEditDto?.parts) {
      return workOrders?.getData?.workOrderEditDto?.parts.some(function (el) {
        return el.id === id;
      });
    } else return false;
  };

  const partsListCallback = (item, field, value) => {
    return {
      label: item[value],
      value: item[field],
      partsInInventory: item['partsInInventory'],
      isDisabled: partExists(item['id']) || item.deleted || item.partsInInventory === 0 ? true : false,
      selectedValue: {},
      company: item['company'],
      cost: item['cost'],
      id: item['id'],
      orderedQty: item['orderedQty'] || 1,
      partName: item['partName'],
    };
  };

  const objectArrayFromDiffFieldsMech = (list, value, field1, field2, callback) => {
    return list?.length
      ? list.map((item) => {
          if (callback) return callback(item, field1, field2, value);
          else
            return {
              label: field2 ? `${item[field1]} ${item[field2]}` : item[field1],
              value: item[value],
            };
        })
      : [];
  };

  const { partsList, trailerList, vehicleList, vehicleMechanicList } = useMemo(() => {
    const partsList = objectArrayFromDiffFields(workOrders?.getData?.parts, 'id', 'partName', partsListCallback);
    const trailerList = objectArrayFromDiffFields(workOrders?.getData?.workOrderEditDto?.trailers, 'trailerId', 'name');
    const vehicleList = objectArrayFromDiffFields(workOrders?.getData?.workOrderEditDto?.vehicles, 'vehicleId', 'name');
    const vehicleMechanicList = objectArrayFromDiffFieldsMech(
      workOrders?.getData?.workOrderEditDto?.allMechanics,
      'id',
      'firstName',
      'lastName'
    );
    return { partsList, trailerList, vehicleList, vehicleMechanicList };
  }, [workOrders]);

  const initialValues = useMemo(() => {
    const data = workOrders?.getData?.workOrderEditDto;
    return {
      idPub: data?.idPub || null,
      reportedDate: data?.reportedDate || '',
      receiptsUploaded: data?.receiptsUploaded || '',
      state: data?.state || 'NOT_STARTED',
      concern: data?.concern || '',
      cause: data?.cause || '',
      correction: data?.correction || '',
      labourCost: data?.labourCost || '0.00',
      cost: data?.cost || '0.00',
      partCost: data?.partCost || '0.00',
      fuel: data?.dailyLiquids ? data.dailyLiquids.filter((ele) => ele.liquidType == 'Fuel')[0].quantity : '',
      engineOil: data?.dailyLiquids ? data.dailyLiquids.filter((ele) => ele.liquidType == 'Engine Oil')[0].quantity : '',
      hydraulicOil: data?.dailyLiquids ? data.dailyLiquids.filter((ele) => ele.liquidType == 'Hydraulic Oil')[0].quantity : '',
      windShieldFluid: data?.dailyLiquids
        ? data.dailyLiquids.filter((ele) => ele.liquidType == 'Windshield Washer Fluid')[0].quantity
        : '',
      beginDate: (data?.beginDate && data?.beginDate.substr(0, data?.beginDate.indexOf(' '))) || '',
      endDate: (data?.endDate && data?.endDate.substr(0, data?.endDate.indexOf(' '))) || '',
      note: data?.note || '',
      parts: data?.parts || [],
      unitId: data?.vehicleIdPub || '',
      mileage: data?.mileage,
      partsArray: data?.parts || [],
      isEdit: type === 'edit' ? true : false,
      mechanicId: vehicleMechanicList?.filter((mech) => mech.value === data?.mechanicId)[0] || '',
      reportId: data?.inspectionDto?.reportId || null,
      pictureId: data?.inspectionDto?.inspectionConditions || [],
    };
  }, [workOrders]);

  const getDailyLiquids = (fields) => [
    {
      liquidType: 'Fuel',
      quantity: fields.fuel,
      unit: '',
    },
    {
      liquidType: 'Engine Oil',
      quantity: fields.engineOil,
      unit: '',
    },
    {
      liquidType: 'Hydraulic Oil',
      quantity: fields.hydraulicOil,
      unit: '',
    },
    {
      liquidType: 'Windshield Washer Fluid',
      quantity: fields.windShieldFluid,
      unit: '',
    },
  ];

  function getWorkOrderRouting(fields) {
    if (!isdvirOnly()) {
      return {
        url: pageUrls.workOrders,
        successMessage: i18n.t('workOrders.workOrderEditSuccess'),
      };
    }

    let url = pageUrls.pendingMechanicInspections;
    const successMessage = i18n.t('workOrders.serviceRequestEditSuccess');

    if (['COMPLETED', 'APPROVED'].includes(fields?.state)) {
      url = pageUrls.pendingDriverInspections;
    } else if (fields?.state === 'CLOSED') {
      url = pageUrls.completedInspections;
    }

    return { url, successMessage };
  }

  function getPayload(fields) {
    const doc = workOrders.documentsList.filter((doc) => doc.id !== null).map((doc) => doc.id);

    const parts = fields.partsArray.map(({ isDisabled, label, partsInInventory, selectedValue, value, ...rest }) => ({
      ...rest,
      orderedQty: Number(rest.orderedQty),
    }));

    return {
      beginDate: fields.beginDate,
      dailyLiquids: getDailyLiquids(fields),
      cause: fields.cause,
      concern: fields.concern,
      correction: fields.correction,
      cost: Number((Math.round(fields.cost * 100) / 100).toFixed(2)),
      endDate: fields.endDate,
      idPub: fields.idPub,
      labourCost: Number((Math.round(fields.labourCost * 100) / 100).toFixed(2)),
      mechanicId: fields?.mechanicId?.value || '',
      mileage: fields.mileage,
      note: fields.note,
      partCost: Number((Math.round(fields.partCost * 100) / 100).toFixed(2)),
      parts: parts,
      receiptsUploaded: doc.join(','),
      reportedDate: moment().format('MM/DD/YYYY'),
      state: fields.state,
      type: type === 'add' ? fields.type.value : fields.type,
      unitType: type === 'add' ? fields.unitType.value : fields.unitType,
      vehicleIdPub: fields.unitId.value,
    };
  }

  const handleSubmit = (fields) => {
    saveAddWorkOrders({
      data: getPayload(fields),
      apiId: 'saveAddWorkOrders',
      callback: callback,
      history,
      ...getWorkOrderRouting(fields),
    });
  };

  const handleCancel = () => {
    let url = pageUrls.workOrders;
    const state = workOrders.getData?.workOrderEditDto?.state;
    if (isdvirOnly()) {
      url = pageUrls.pendingMechanicInspections;
      if (state && ['COMPLETED'].includes(state)) url = pageUrls.pendingDriverInspections;
      if (state && ['CLOSED', 'APPROVED'].includes(state)) url = pageUrls.completedInspections;
    }
    history.push(url);
  };

  return (
    <>
      <Form
        type="server"
        direction="column"
        initialValues={{ ...initialValues }}
        RenderComponent={RenderComponent}
        handleOnSubmit={handleSubmit}
        customProps={{
          apiStatus,
          callback,
          Styled,
          workOrders,
          partsList,
          saveDocument,
          deleteDocument,
          setInitialValues: () => {},
          setValidationlist: () => {},
          downloadDocument,
          vehicleMechanicList,
          trailerList,
          vehicleList,
          logBook,
          getDocumentToken,
          viewReport,
          i18n,
          setOpenPhoto,
          ModalObjectPhoto,
          setModalObjectPhoto,
          viewOnly,
        }}
        validationSchema={
          type === 'add'
            ? object().shape({
                ...schema,
              })
            : null
        }
        handleOnCancel={handleCancel}
      />
      <iframe id="downloadDocument" title="downloadDocument" style={{ display: 'none' }}></iframe>
    </>
  );
};

WorkOrderForm.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  saveUserData: PropTypes.func.isRequired,
  saveDocument: PropTypes.func.isRequired,
  deleteDocument: PropTypes.func.isRequired,
  fetchTerminals: PropTypes.func.isRequired,
  fetchDepartments: PropTypes.func.isRequired,
  fetchSubDepartments: PropTypes.func.isRequired,
  regions: PropTypes.array.isRequired,
  types: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  handleDownloadDoc: PropTypes.func.isRequired,
};

export default WorkOrderForm;
